'use client'

import React, { useEffect, useState, useRef } from 'react'
import { PatternDrawing } from '../types/drawing'
import { generateTradingRecommendation, TradingRecommendation } from '../utils/tradingRecommendations'

interface PatternNotificationProps {
  patterns: PatternDrawing[]
  currentPrice: number
  onClose: () => void
}

const PatternNotification: React.FC<PatternNotificationProps> = ({
  patterns,
  currentPrice,
  onClose
}) => {
  const [recommendation, setRecommendation] = useState<TradingRecommendation | null>(null)
  const [isVisible, setIsVisible] = useState(true)
  const [showMoreInfo, setShowMoreInfo] = useState(false)
  const [timerPaused, setTimerPaused] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(15000) // 15 seconds
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (patterns.length > 0) {
      const rec = generateTradingRecommendation(patterns, currentPrice)
      setRecommendation(rec)
    }
  }, [patterns, currentPrice])

  // Auto-close timer with pause/resume functionality
  useEffect(() => {
    if (!timerPaused && timeRemaining > 0) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 100 // Update every 100ms for smooth animation
          if (newTime <= 0) {
            handleClose()
            return 0
          }
          return newTime
        })
      }, 100)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [timeRemaining, timerPaused]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    console.log('🔴 Pattern Notification Close Button Clicked')
    setIsVisible(false)
    setTimeout(onClose, 300) // Allow fade out animation
  }

  // Handle More Info toggle with timer pause/resume
  const handleMoreInfoToggle = () => {
    const newShowMoreInfo = !showMoreInfo
    setShowMoreInfo(newShowMoreInfo)
    setTimerPaused(newShowMoreInfo) // Pause timer when expanded, resume when collapsed
  }

  if (!recommendation || !isVisible) return null

  const getActionColor = (action: string) => {
    switch (action) {
      case 'BUY': return 'text-green-400 bg-green-500/20'
      case 'SELL': return 'text-red-400 bg-red-500/20'
      case 'HOLD': return 'text-blue-400 bg-blue-500/20'
      case 'WAIT': return 'text-yellow-400 bg-yellow-500/20'
      case 'CAUTION': return 'text-orange-400 bg-orange-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'HIGH': return 'text-green-400'
      case 'MEDIUM': return 'text-yellow-400'
      case 'LOW': return 'text-red-400'
      default: return 'text-gray-400'
    }
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'LOW': return 'text-green-400'
      case 'MEDIUM': return 'text-yellow-400'
      case 'HIGH': return 'text-red-400'
      default: return 'text-gray-400'
    }
  }

  // Helper function to get detailed pattern explanation
  const getPatternExplanation = (patternType: string) => {
    switch (patternType) {
      case 'head-and-shoulders':
        return {
          title: 'Head and Shoulders Pattern',
          description: 'A bearish reversal pattern formed after an uptrend.',
          logic: [
            '🔸 Left Shoulder: First peak as buying interest starts to wane',
            '🔸 Head: Higher peak as last push higher fails to sustain',
            '🔸 Right Shoulder: Lower peak showing weakening momentum',
            '🔸 Neckline: Support level connecting the two valleys',
            '🔸 Breakout: Price breaks below neckline confirming reversal'
          ],
          tradingMechanics: [
            '📈 Entry: When price breaks below neckline with volume',
            '🎯 Target: Distance from head to neckline projected downward',
            '🛡️ Stop Loss: Above the right shoulder peak',
            '⏰ Confirmation: Volume should increase on neckline break'
          ]
        }
      
      case 'double-top':
        return {
          title: 'Double Top Pattern',
          description: 'A bearish reversal pattern showing failed attempts to break resistance.',
          logic: [
            '🔸 First Peak: Initial attempt to break higher fails',
            '🔸 Valley: Pullback as sellers take control temporarily',
            '🔸 Second Peak: Another attempt at same resistance level fails',
            '🔸 Neckline: Support level at the valley between peaks',
            '🔸 Pattern confirms when price breaks below neckline'
          ],
          tradingMechanics: [
            '📈 Entry: When price breaks below valley (neckline) support',
            '🎯 Target: Distance from peaks to valley projected downward',
            '🛡️ Stop Loss: Above the second peak',
            '⏰ Volume: Should decrease at second peak, increase on breakdown'
          ]
        }
      
      case 'double-bottom':
        return {
          title: 'Double Bottom Pattern',
          description: 'A bullish reversal pattern showing strong support holding twice.',
          logic: [
            '🔸 First Valley: Initial selling pressure reaches support',
            '🔸 Peak: Relief rally as buyers step in',
            '🔸 Second Valley: Another test of same support level holds',
            '🔸 Neckline: Resistance level at the peak between valleys',
            '🔸 Pattern confirms when price breaks above neckline'
          ],
          tradingMechanics: [
            '📈 Entry: When price breaks above peak (neckline) resistance',
            '🎯 Target: Distance from valleys to peak projected upward',
            '🛡️ Stop Loss: Below the second valley',
            '⏰ Volume: Should increase on breakout above neckline'
          ]
        }
      
      case 'ascending-triangle':
        return {
          title: 'Ascending Triangle Pattern',
          description: 'A bullish continuation pattern with flat resistance and rising support.',
          logic: [
            '🔸 Flat Resistance: Horizontal line connecting multiple peaks',
            '🔸 Rising Support: Upward trending line connecting higher lows',
            '🔸 Convergence: Lines meet forming triangle shape',
            '🔸 Volume: Typically decreases during formation',
            '🔸 Breakout: Expected upward through resistance line'
          ],
          tradingMechanics: [
            '📈 Entry: When price breaks above resistance with volume',
            '🎯 Target: Triangle height added to breakout point',
            '🛡️ Stop Loss: Below the last higher low',
            '⏰ Volume: Must increase significantly on breakout'
          ]
        }
      
      case 'descending-triangle':
        return {
          title: 'Descending Triangle Pattern',
          description: 'A bearish continuation pattern with flat support and falling resistance.',
          logic: [
            '🔸 Flat Support: Horizontal line connecting multiple valleys',
            '🔸 Falling Resistance: Downward trending line connecting lower highs',
            '🔸 Convergence: Lines meet forming inverted triangle',
            '🔸 Volume: Typically decreases during formation',
            '🔸 Breakout: Expected downward through support line'
          ],
          tradingMechanics: [
            '📈 Entry: When price breaks below support with volume',
            '🎯 Target: Triangle height subtracted from breakout point',
            '🛡️ Stop Loss: Above the last lower high',
            '⏰ Volume: Must increase significantly on breakout'
          ]
        }
      
      case 'symmetrical-triangle':
        return {
          title: 'Symmetrical Triangle Pattern',
          description: 'A neutral continuation pattern with converging trendlines.',
          logic: [
            '🔸 Upper Trendline: Connects series of lower highs',
            '🔸 Lower Trendline: Connects series of higher lows',
            '🔸 Convergence: Both lines slope toward each other',
            '🔸 Volume: Decreases as pattern develops',
            '🔸 Breakout: Can be in either direction with volume'
          ],
          tradingMechanics: [
            '📈 Entry: Direction of breakout with increased volume',
            '🎯 Target: Triangle height from breakout point',
            '🛡️ Stop Loss: Opposite side of triangle',
            '⏰ Timing: Breakout usually occurs in final third of pattern'
          ]
        }
      
      default:
        return {
          title: 'Pattern Analysis',
          description: 'Technical pattern detected on the chart.',
          logic: [
            '🔸 Price action has formed a recognizable pattern',
            '🔸 Pattern provides insights into market psychology',
            '🔸 Can indicate potential future price direction',
            '🔸 Should be combined with other technical indicators'
          ],
          tradingMechanics: [
            '📈 Entry: Based on pattern completion and confirmation',
            '🎯 Target: Calculated based on pattern measurements',
            '🛡️ Risk Management: Always use appropriate stop losses',
            '⏰ Confirmation: Wait for volume and price confirmation'
          ]
        }
    }
  }

  const patternCount = patterns.length
  const primaryPattern = patterns[0]
  const patternExplanation = getPatternExplanation(primaryPattern.type)

  return (
    <div className={`fixed top-4 right-4 z-[10002] transition-all duration-300 ${
      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
    }`}>
      <div className="bg-gray-900/95 border border-gray-700 rounded-lg p-4 shadow-2xl backdrop-blur-sm min-w-[380px] max-w-[450px]">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <h3 className="text-white font-semibold text-sm">
              Pattern Detection Alert
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-300 hover:text-white hover:bg-red-500/30 transition-all duration-200 rounded-full w-7 h-7 flex items-center justify-center font-bold text-base cursor-pointer relative"
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
            title="Close notification"
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            ×
          </button>
        </div>

        {/* Pattern Info */}
        <div className="mb-4">
          <div className="text-gray-300 text-sm mb-1">
            {patternCount === 1 ? (
              <>Pattern detected: <span className="text-blue-400 font-medium">{primaryPattern.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span></>
            ) : (
              <>{patternCount} patterns detected, primary: <span className="text-blue-400 font-medium">{primaryPattern.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span></>
            )}
          </div>
        </div>

        {/* Trading Recommendation */}
        <div className="bg-gray-800/50 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-300 text-sm font-medium">Suggested Action:</span>
            <div className={`px-2 py-1 rounded text-xs font-bold ${getActionColor(recommendation.action)}`}>
              {recommendation.action}
            </div>
          </div>
          
          <div className="text-gray-300 text-sm mb-3 leading-relaxed">
            {recommendation.reasoning}
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-gray-400">Confidence:</span>
              <span className={`ml-1 font-medium ${getConfidenceColor(recommendation.confidence)}`}>
                {recommendation.confidence}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Risk Level:</span>
              <span className={`ml-1 font-medium ${getRiskColor(recommendation.riskLevel)}`}>
                {recommendation.riskLevel}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Timeframe:</span>
              <span className="ml-1 text-white font-medium">
                {recommendation.timeframe}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Current Price:</span>
              <span className="ml-1 text-white font-medium">
                ${currentPrice.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Price Targets */}
          {(recommendation.targetPrice || recommendation.stopLoss) && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="grid grid-cols-2 gap-3 text-xs">
                {recommendation.targetPrice && (
                  <div>
                    <span className="text-gray-400">Target Price:</span>
                    <div className="text-green-400 font-medium">
                      ${recommendation.targetPrice.toFixed(2)}
                      <span className="text-green-300 ml-1">
                        ({((recommendation.targetPrice - currentPrice) / currentPrice * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                )}
                {recommendation.stopLoss && (
                  <div>
                    <span className="text-gray-400">Stop Loss:</span>
                    <div className="text-red-400 font-medium">
                      ${recommendation.stopLoss.toFixed(2)}
                      <span className="text-red-300 ml-1">
                        ({((recommendation.stopLoss - currentPrice) / currentPrice * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* More Info Button */}
        <div className="mb-3">
          <button
            onClick={handleMoreInfoToggle}
            className={`w-full border text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 px-3 py-2 rounded-lg ${
              timerPaused 
                ? 'bg-yellow-600/20 hover:bg-yellow-600/30 border-yellow-500/50 text-yellow-400' 
                : 'bg-blue-600/20 hover:bg-blue-600/30 border-blue-500/50 text-blue-400'
            }`}
          >
            <span>{showMoreInfo ? '📖' : '📚'} {showMoreInfo ? 'Hide Info' : 'More Info'}</span>
            {timerPaused && <span className="text-xs">(Timer Paused)</span>}
            <span className={`transform transition-transform duration-200 ${showMoreInfo ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
        </div>

        {/* Expandable Pattern Explanation */}
        {showMoreInfo && (
          <div className="bg-gray-800/50 rounded-lg p-4 mb-3 border border-gray-700 animate-in slide-in-from-top duration-300">
            <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
              <span>🎯</span> {patternExplanation.title}
            </h4>
            
            <p className="text-gray-300 text-xs mb-3 leading-relaxed">
              {patternExplanation.description}
            </p>

            {/* Pattern Logic */}
            <div className="mb-3">
              <h5 className="text-gray-300 font-medium text-xs mb-2">📊 Pattern Logic:</h5>
              <div className="space-y-1">
                {patternExplanation.logic.map((item, index) => (
                  <div key={index} className="text-gray-400 text-xs flex items-start gap-1">
                    <span className="text-blue-400 mt-0.5 text-[10px]">•</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trading Mechanics */}
            <div className="mb-3">
              <h5 className="text-gray-300 font-medium text-xs mb-2">⚙️ Trading Mechanics:</h5>
              <div className="space-y-1">
                {patternExplanation.tradingMechanics.map((item, index) => (
                  <div key={index} className="text-gray-400 text-xs flex items-start gap-1">
                    <span className="text-green-400 mt-0.5 text-[10px]">•</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pattern Quality Indicators */}
            <div className="bg-gray-900/50 rounded p-2 border border-gray-600">
              <h5 className="text-gray-300 font-medium text-xs mb-2">🔍 Quality Indicators:</h5>
              <div className="grid grid-cols-1 gap-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Pattern Age:</span>
                  <span className="text-white">
                    {Math.floor((Date.now() - primaryPattern.createdAt) / (1000 * 60 * 60 * 24))} days
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Pattern Points:</span>
                  <span className="text-white">{primaryPattern.points.length} points</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Visibility:</span>
                  <span className={`${primaryPattern.isVisible ? 'text-green-400' : 'text-red-400'}`}>
                    {primaryPattern.isVisible ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
            </div>

            {/* Educational Note */}
            <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs">
              <div className="flex items-start gap-2">
                <span className="text-yellow-400 mt-0.5">💡</span>
                <div className="text-yellow-300">
                  <span className="font-medium">Educational Note:</span> Pattern recognition is a tool, not a guarantee. 
                  Always combine with other analysis, proper risk management, and consider market context.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="text-xs text-gray-500 italic">
          💡 This is educational analysis only. Please do your own research before making trading decisions.
        </div>

        {/* Auto-close timer */}
        <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-100 ${timerPaused ? 'bg-yellow-500' : 'bg-blue-500'}`}
            style={{ 
              width: `${(timeRemaining / 15000) * 100}%`,
              transition: timerPaused ? 'none' : 'width 100ms linear'
            }}
          />
        </div>
        
        {/* Timer Status Indicator */}
        {showMoreInfo && (
          <div className="mt-1 text-center">
            <span className="text-xs text-yellow-400 flex items-center justify-center gap-1">
              <span>⏸️</span>
              Timer paused - Take your time reading!
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default PatternNotification 